/**
 * 리포트 댓글 작성 추적 훅
 * 
 * 리포트 상세 페이지에서 의견/댓글을 작성하는 기능
 * - start()는 세션 스토리지를 확인하여 저장된 의도가 있으면 자동 복원
 * - 로그인 후 리다이렉트되어도 같은 의도 유지
 * - 로그인 의도는 자동으로 부모 의도 연결 (parentIntentType)
 * 
 * @example
 * ```tsx
 * function CommentForm({ reportId, parentCommentId }) {
 *   const reportComment = useReportComment({ 
 *     report_id: reportId,
 *     parent_comment_id: parentCommentId  // 대댓글인 경우에만
 *   });
 *   const loginIntent = useLoginIntent();
 *   const isLoggedIn = useAuth().isLoggedIn;
 *   
 *   const handleSubmit = async (content: string) => {
 *     reportComment.start(); // 의도 시작
 *     
 *     if (!isLoggedIn) {
 *       // 로그인 의도 시작 (부모 의도 자동 연결)
 *       loginIntent.start(
 *         { redirectUrl: router.asPath },
 *         { parentIntentType: 'report_comment' }  // 👈 타입으로 부모 의도 자동 조회
 *       );
 *       router.push('/login');
 *       return;
 *     }
 *     
 *     try {
 *       await apiCreateComment(reportId, content, parentCommentId);
 *       reportComment.success();
 *     } catch (error) {
 *       reportComment.fail('backend_5xx');
 *     }
 *   };
 *   
 *   return (
 *     <form onSubmit={(e) => {
 *       e.preventDefault();
 *       handleSubmit(e.target.content.value);
 *     }}>
 *       <textarea name="content" placeholder="의견을 작성하세요" />
 *       <button type="submit">작성</button>
 *     </form>
 *   );
 * }
 * ```
 */

import { useCallback } from 'react';
import { track } from '../../../../core/client';
import { useIntent } from '../useIntent';

export interface ReportCommentContext {
    report_id: number;
    parent_comment_id?: number;  // 대댓글인 경우에만
}

export interface ReportCommentActions {
    start: (context?: ReportCommentContext) => void;
    success: () => void;
    fail: (reason?: string) => void;
    blocked: (reason: string) => void;
}

/**
 * 리포트 댓글 작성 추적 훅
 */
export function useReportComment(
    initialContext?: ReportCommentContext
): ReportCommentActions {
    const { report_id, parent_comment_id } = initialContext || {};

    const intent = useIntent<ReportCommentContext>({
        type: 'report_comment',
        requiredGate: 'login',
        onStart: ({ intent_id, context, parent_intent_id, required_gate }) => {
            track('report_comment_start', {
                intent_id,
                report_id: context?.report_id || report_id || 0,
                parent_comment_id: context?.parent_comment_id !== undefined
                    ? context.parent_comment_id
                    : parent_comment_id || 0,
                parent_intent_id,
                required_gate,
            });
        },
        onSuccess: ({ intent_id, context, latency_ms }) => {
            track('report_comment_success', {
                intent_id,
                report_id: context?.report_id || report_id || 0,
                parent_comment_id: context?.parent_comment_id !== undefined
                    ? context.parent_comment_id
                    : parent_comment_id,
                latency_ms,
            });
        },
        onFail: ({ intent_id, context, fail_reason }) => {
            track('report_comment_fail', {
                intent_id,
                report_id: context?.report_id || report_id || 0,
                parent_comment_id: context?.parent_comment_id !== undefined
                    ? context.parent_comment_id
                    : parent_comment_id,
                fail_reason,
            });
        },
        onBlocked: ({ intent_id, context, block_reason }) => {
            track('report_comment_blocked', {
                intent_id,
                report_id: context?.report_id || report_id || 0,
                parent_comment_id: context?.parent_comment_id !== undefined
                    ? context.parent_comment_id
                    : parent_comment_id,
                block_reason,
            });
        },
    });

    /**
     * 댓글 작성 의도 시작
     */
    const start = useCallback((context?: ReportCommentContext) => {
        intent.start(context || initialContext);
    }, [intent, initialContext]);

    /**
     * 댓글 작성 성공
     */
    const success = useCallback(() => {
        intent.success();
    }, [intent]);

    /**
     * 댓글 작성 실패
     */
    const fail = useCallback((reason?: string) => {
        intent.fail(reason);
    }, [intent]);

    /**
     * 댓글 작성 이벤트 발생 차단
     */
    const blocked = useCallback((reason: string) => {
        intent.blocked(reason);
    }, [intent]);

    return {
        start,
        success,
        fail,
        blocked,
    };
}

